// recoveryGenerations — 荒らし復旧 UI 第 1 段階「世代を選んで戻す」の pure ヘルパ
// (vandalism-recovery-ui-2026-09-27.md §2「詳細設計」+ 追補「範囲の修正」+
// 「入口を『続きを描く』に統一」)。
//
// DOM / fetch に触れない純関数のみ (= vitest node で全分岐テスト可能)。

import type { PastCanvasKind } from '../infrastructure/pastCanvas/pastCanvasClient.js';
import type { RecoveryGeneration } from '../infrastructure/pastCanvas/recoveryGenerationsClient.js';

/**
 * `?recover=<seq>` の初期選択インデックス (§2「開くと選択画面が『seq より前の
 * 最後の世代』を選んだ状態で開く」、anti-vandalism-2026-09-24.md §5「1 →
 * `?recover=<seq>`」)。
 *
 * **入口統一 (2026-09-28) で用途を整理**: 文書切り替え UI が無くなったため、この
 * 関数は「開かれた文書の中で `?recover=<seq>` に一致する世代を探す」用途に
 * 一本化した (通常入口では呼ばれない — `RecoveryGenerationPickerModal` は
 * `initialSeq` 未指定時は常に右端 (= 保存時点) を初期選択する)。
 *
 * `recoverSeq` が `undefined` なら `null` (= 呼び出し不要ケースの安全側デフォルト)。
 * `generations` は seq 昇順を前提とする (サーバ `GET .../generations` の契約)。
 * `recoverSeq` 未満の最後の要素 (= 最も近い過去の世代) の index を返す。無ければ
 * `null` (呼び出し側は右端 + 案内文にフォールバックする、最新世代への暗黙
 * フォールバックはしない)。
 */
export const pickInitialGenerationIndex = (
  generations: readonly RecoveryGeneration[],
  recoverSeq: number | undefined,
): number | null => {
  if (recoverSeq === undefined) return null;
  let found: number | null = null;
  for (let i = 0; i < generations.length; i += 1) {
    const g = generations[i];
    if (g === undefined) continue;
    if (g.seq < recoverSeq) {
      found = i;
    } else {
      break;
    }
  }
  return found;
};

/**
 * 同じ中身の世代の重複除去 (範囲の修正 §2「同じ中身の世代の重複」)。
 * `isSeedCopy: true` (= 他世代の画素への pointer だけで構成される世代、典型は
 * 復元直後の seed 世代) をスライダーの選択対象から除く。サーバ側では畳まず印だけ
 * 付ける設計 (選択可能性を保つ) なので、この判断は web 側で行う。
 */
export const excludeSeedCopyGenerations = (
  generations: readonly RecoveryGeneration[],
): readonly RecoveryGeneration[] => generations.filter((g) => !g.isSeedCopy);

/**
 * `?recover=` query の raw 文字列 → 非負整数 seq。パース不能 (欠落 / 非数値 /
 * 負数 / 小数) は `null` (= 深リンク扱いしない、§2「非オーナーなら何もしない」と
 * 同じ穏やかな degrade)。
 */
export const parseRecoverQueryParam = (raw: string | null): number | null => {
  if (raw === null) return null;
  if (!/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) ? n : null;
};

/**
 * 一覧の「種類」表示 (§2 確定 4「行に種類を持たせ、一覧に出す」)。`null` (=
 * 不明・カラム追加以前の既存行) は何も出さない (呼び出し側は `null` を空文字として
 * 扱い、ラベル自体を描画しない)。
 */
const PAST_CANVAS_KIND_LABELS: Record<PastCanvasKind, string> = {
  cleared: '全消し',
  'restore-autosave': '復元前の自動保存',
  picked: '時点を選んで作成',
  // 荒らし復旧 第 2 段階: 人 / ストロークを外して焼いた行 (作者のみ作れる)。
  excluded: '人を外して作成',
};

export const pastCanvasKindLabel = (kind: PastCanvasKind | null): string | null =>
  kind === null ? null : PAST_CANVAS_KIND_LABELS[kind];

// ── 入口統一 (2026-09-28): 過去キャンバス一覧の各行から選択画面を開く ─────────

/**
 * `?recover=<seq>` が開くべき行 (= 文書) を決める (入口統一 §2「`?recover=<seq>`
 * の文書は一覧の `clearedSeq` で決める」)。
 *
 * `clearedSeq >= seq` を満たす行のうち `clearedSeq` が最小のもの — その行が
 * 指す文書が「seq の時点でまだアクティブだった最も早く終わった文書」(= seq
 * 前後の荒らしが実際に起きた文書に最も近い候補) になる。該当が無ければ `null`
 * を返し、呼び出し側は「今の絵」(現行文書) を対象にする。
 *
 * 複数行が同じ `clearedSeq` を持つ (= 同じ文書を指す複数行、例: 全消し行 +
 * その後の picked 行) 場合は、渡された配列内で最初に見つかったものを返す
 * (安定ソートに依存しない単純な最小値探索)。
 */
export const pickRecoverTargetRow = <T extends { readonly clearedSeq: number }>(
  items: readonly T[],
  seq: number,
): T | null => {
  let best: T | null = null;
  for (const item of items) {
    if (item.clearedSeq < seq) continue;
    if (best === null || item.clearedSeq < best.clearedSeq) best = item;
  }
  return best;
};

/**
 * 同じ文書 (`sessionId`) を指すカード同士に付ける印 (入口統一 §2「同じ文書を
 * 指すカードに同じ印」)。1 件しか無い `sessionId` は印を付けない (= 「同じ文書が
 * 複数ある」ことに気づかせるための印であって、単独行には意味が無い)。
 *
 * 返り値は `sessionId → マーク index` (0 始まり、一覧内での初出順)。呼び出し側は
 * この index を固定パレット (色 等) に `% パレット長` で当てる。印が無い
 * `sessionId` は `Map` に現れない。
 */
export const assignSessionMarks = (
  items: readonly { readonly sessionId: string }[],
): ReadonlyMap<string, number> => {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(item.sessionId, (counts.get(item.sessionId) ?? 0) + 1);
  const marks = new Map<string, number>();
  let next = 0;
  for (const item of items) {
    if ((counts.get(item.sessionId) ?? 0) < 2) continue;
    if (marks.has(item.sessionId)) continue;
    marks.set(item.sessionId, next);
    next += 1;
  }
  return marks;
};

/**
 * ピッカーが表示する世代点 (= `RecoveryGeneration` + 表示/確定に使う 2 フラグ)。
 *
 * - `isRowPoint`: この点が「開いた行そのもの」(= 行の `snapshotSeq`) を表すか。
 *   `true` のときラベルは日時でなく「保存時点」(入口統一 §2「右端のラベルは
 *   『保存時点』」— 最終 snapshot 以降の描画は含まれないため、精密な時刻に
 *   見せない)。
 * - `isSynthetic`: 世代一覧に行の `snapshotSeq` と一致する要素が無かった
 *   (= seed 世代として除外された / R2 に無い 等) ため web 側で合成した点か。
 *   `true` のときだけ確定は POST を経由せず、行の既知 `pastCanvasId` で直接
 *   `whiteboard.restore-request` を送る (入口統一 §2「確定は既存 pastCanvasId
 *   で直接 restore-request」)。プレビューも `generations/:seq/snapshot-urls`
 *   ではなく既存 `past-canvases/:id/snapshot-urls` を使う。
 */
export interface PickerGeneration extends RecoveryGeneration {
  readonly isRowPoint: boolean;
  readonly isSynthetic: boolean;
}

/**
 * 行から開いた選択画面の「対象行」情報。`RecoveryGenerationPickerModal` が
 * 呼び出し元 (`PastCanvasListModal`) から受け取る。「今の絵」から開いたときは
 * 呼び出し元がこの引数自体を渡さない (= 上限なし、行そのもの点も無い)。
 */
export interface RecoveryPickerRow {
  readonly pastCanvasId: string;
  readonly snapshotSeq: number;
  /**
   * 行の文書の終端 seq (= 一覧の `clearedSeq`)。第 2 段階「人を外して戻す」が
   * 「その行の文書の系譜」を絵ログから辿る終端に使う。省略時は `snapshotSeq`。
   */
  readonly clearedSeq?: number;
  /** 行の種類。`excluded` の行は「人を外して戻す」の対象外 (サーバも `target_not_supported`)。 */
  readonly kind?: PastCanvasKind | null;
  /** 行そのもの点が合成 (`isSynthetic`) だったときのラベル用 (未使用だが保持)。 */
  readonly clearedAt: string;
}

/**
 * 世代一覧 (サーバ応答そのまま) から、ピッカーが実際に表示する点の配列を組み立てる
 * (入口統一 §2「スライダー」)。
 *
 * 1. `isSeedCopy` を除外する (既存、範囲の修正)。
 * 2. `row` があれば `seq <= row.snapshotSeq` に絞る (= 行より後の世代 — 別の
 *    文書にしか存在し得ない — を混ぜない)。
 * 3. `row` があり、絞った結果に `seq === row.snapshotSeq` の要素が無ければ、
 *    右端に「行そのもの」の合成点を足す (`isSynthetic: true`)。世代 API が
 *    404/503 だった場合は呼び出し側が `generations: []` を渡せば、この 1 点
 *    だけで動く (= 従来の「続きを描く」と同じ耐性)。
 * 4. `row` の `snapshotSeq` に一致する要素があれば、その要素を `isRowPoint:
 *    true` にマークする (ラベルは「保存時点」になるが確定は通常どおり POST 経由
 *    — 既存行なら冪等でそれが返る)。
 *
 * `row` が無い (「今の絵」から開いた) 場合は seed-copy 除外だけを行い、
 * 全要素 `isRowPoint`/`isSynthetic` ともに `false`。
 */
export const buildPickerGenerations = (
  generations: readonly RecoveryGeneration[],
  row: RecoveryPickerRow | undefined,
): readonly PickerGeneration[] => {
  const withoutSeedCopies = excludeSeedCopyGenerations(generations);
  if (row === undefined) {
    return withoutSeedCopies.map((g) => ({ ...g, isRowPoint: false, isSynthetic: false }));
  }
  const bounded = withoutSeedCopies.filter((g) => g.seq <= row.snapshotSeq);
  const tagged: PickerGeneration[] = bounded.map((g) => ({
    ...g,
    isRowPoint: g.seq === row.snapshotSeq,
    isSynthetic: false,
  }));
  if (!tagged.some((g) => g.isRowPoint)) {
    tagged.push({
      seq: row.snapshotSeq,
      savedAt: row.clearedAt,
      isSeedCopy: false,
      isRowPoint: true,
      isSynthetic: true,
    });
  }
  return tagged;
};
